import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { formatSalespersonCode } from './accountCollection';

export type FormFieldOption = {
  id: string;
  form_slug: string;
  field_key: string;
  value: string;
  label: string;
  sort_order: number;
  active: boolean;
};

export type ApplicationNotificationSettings = {
  ar_email: string;
  default_sales_email: string;
  notifications_enabled: boolean;
  submission_customer_subject: string;
  submission_customer_body: string;
  submission_finance_subject: string;
  submission_finance_body: string;
  submission_internal_subject: string;
  submission_internal_body: string;
  submission_sales_subject: string;
  submission_sales_body: string;
  approved_customer_subject: string;
  approved_customer_body: string;
  approved_internal_subject: string;
  approved_internal_body: string;
  approved_sales_subject: string;
  approved_sales_body: string;
  rejected_internal_subject: string;
  rejected_internal_body: string;
};

export type SalespersonEmailRow = {
  code: string;
  email: string;
  display_name: string | null;
  active: boolean;
  sort_order: number;
};

export type ApplicationFormSharePointSettings = {
  enabled: boolean;
  site_hostname: string;
  site_path: string;
  folder_path: string;
  workbook_name: string;
  sheet_name: string;
  cff_enabled?: boolean;
  cff_site_hostname?: string | null;
  cff_site_path?: string | null;
  cff_folder_path?: string | null;
  cff_last_sync_at?: string | null;
  cff_last_sync_error?: string | null;
  current_workbook_item_id: string | null;
  last_sync_at: string | null;
  last_sync_error: string | null;
  updated_at?: string;
};

const DEFAULT_NOTIFICATION_SETTINGS: ApplicationNotificationSettings = {
  ar_email: '',
  default_sales_email: '',
  notifications_enabled: true,
  submission_customer_subject: 'Application received — {application_no}',
  submission_customer_body:
    'Dear Customer,\n\nThank you. We have received your New Customer Application ({application_no}) for {company_name}.\n\nOur Finance team will review your application and contact you if needed.\n\nBan Teck Han Enterprise Co. Pte. Ltd.',
  submission_finance_subject: '[Review] New account application — {application_no}',
  submission_finance_body:
    'A new account creation application requires review.\n\nRef: {application_no}\nCompany: {company_name}\nSource: {source}\nSubmitted by: {submitted_by}',
  submission_internal_subject: 'Application submitted on behalf of customer — {application_no}',
  submission_internal_body:
    'You submitted application {application_no} for {company_name} on behalf of the customer.\n\nThe review team has been notified.',
  submission_sales_subject: '[Sales] New account application — {application_no}',
  submission_sales_body:
    'A new account creation application has been submitted.\n\nRef: {application_no}\nCompany: {company_name}\nSource: {source}\nSalesperson: {salesperson_code}',
  approved_customer_subject: 'Application approved — {application_no}',
  approved_customer_body:
    'Dear Customer,\n\nYour New Customer Application ({application_no}) for {company_name} has been approved.\n\nCustomer code: {customer_code}\nDelivery schedule: {delivery_schedule}\n\nBan Teck Han Enterprise Co. Pte. Ltd.',
  approved_internal_subject: 'Application approved — {application_no}',
  approved_internal_body:
    'Application {application_no} for {company_name} has been approved.\n\nCustomer code: {customer_code}\nDelivery schedule: {delivery_schedule}',
  approved_sales_subject: 'Application approved — {application_no}',
  approved_sales_body:
    'Application {application_no} for {company_name} has been approved.\n\nCustomer code: {customer_code}\nDelivery schedule: {delivery_schedule}\nSalesperson: {salesperson_code}',
  rejected_internal_subject: 'Application rejected — {application_no}',
  rejected_internal_body:
    'Application {application_no} for {company_name} has been rejected.\n\nReason: {review_notes}\nSalesperson: {salesperson_code}',
};

export const FORM_FIELD_GROUPS: { key: string; label: string }[] = [
  { key: 'apply_for', label: 'Apply for' },
  { key: 'billing_soa', label: 'Billing & SOA' },
  { key: 'payment_terms', label: 'Payment terms (Finance)' },
  { key: 'businessgroup_code', label: 'Business group (Finance)' },
  // salesperson_code is managed via User management → Salesperson (not here)
  { key: 'customer_price_group', label: 'Customer price group (Finance)' },
];

export async function fetchFormFieldOptions(formSlug = 'account-creation'): Promise<FormFieldOption[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('list_form_field_options', { p_form_slug: formSlug });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as FormFieldOption[];
}

export function groupFieldOptions(rows: FormFieldOption[]): Record<string, FormFieldOption[]> {
  const grouped: Record<string, FormFieldOption[]> = {};
  for (const row of rows) {
    if (!grouped[row.field_key]) grouped[row.field_key] = [];
    grouped[row.field_key].push(row);
  }
  return grouped;
}

export async function fetchNotificationSettings(): Promise<ApplicationNotificationSettings> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from('system_config')
    .select('data')
    .eq('key', 'notification_settings')
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  const merged = { ...DEFAULT_NOTIFICATION_SETTINGS, ...((data?.data ?? {}) as Partial<ApplicationNotificationSettings>) };
  return patchLegacyNotificationSubjects(merged);
}

/** Older installs used the same subject for Finance and Sales — distinguish them in memory. */
function patchLegacyNotificationSubjects(
  settings: ApplicationNotificationSettings,
): ApplicationNotificationSettings {
  const legacy = 'New account application — {application_no}';
  const out = { ...settings };
  if (out.submission_finance_subject === legacy) {
    out.submission_finance_subject = DEFAULT_NOTIFICATION_SETTINGS.submission_finance_subject;
  }
  if (out.submission_sales_subject === legacy) {
    out.submission_sales_subject = DEFAULT_NOTIFICATION_SETTINGS.submission_sales_subject;
  }
  return out;
}

export async function saveNotificationSettings(
  input: Partial<ApplicationNotificationSettings>,
): Promise<ApplicationNotificationSettings> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('save_notification_settings', { p_data: input });
  if (error) throw new Error(formatSupabaseError(error));
  return { ...DEFAULT_NOTIFICATION_SETTINGS, ...((data?.data ?? {}) as Partial<ApplicationNotificationSettings>) };
}

export async function fetchSalespersonEmails(): Promise<SalespersonEmailRow[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from('salesperson_emails')
    .select('code, email, display_name, active, sort_order')
    .order('sort_order')
    .order('code');
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as SalespersonEmailRow[];
}

/** Active master list from User Management (sort_order), spaced canonical codes. */
export async function fetchActiveSalespersonCodes(): Promise<string[]> {
  const rows = await fetchSalespersonEmails();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    if (!row.active) continue;
    const formatted = formatSalespersonCode(row.code);
    if (!formatted) continue;
    const key = formatted.replace(/\s+/g, '').toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(formatted);
  }
  return out;
}

export async function upsertSalespersonEmail(row: {
  code: string;
  email?: string;
  display_name?: string;
  active?: boolean;
  sort_order?: number;
}): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('upsert_salesperson_email', {
    p_code: formatSalespersonCode(row.code),
    p_email: (row.email ?? '').trim(),
    p_display_name: null,
    p_active: row.active ?? true,
    p_sort_order: row.sort_order ?? 0,
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function renameSalespersonCode(oldCode: string, newCode: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('rename_salesperson_code', {
    p_old_code: oldCode.trim(),
    p_new_code: formatSalespersonCode(newCode),
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function deleteSalespersonEmail(code: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('delete_salesperson_email', { p_code: code.trim() });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function upsertFormFieldOption(input: {
  id?: string;
  form_slug?: string;
  field_key: string;
  value: string;
  label: string;
  sort_order?: number;
  active?: boolean;
}): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('upsert_form_field_option', {
    p_id: input.id ?? null,
    p_form_slug: input.form_slug ?? 'account-creation',
    p_field_key: input.field_key,
    p_value: input.value,
    p_label: input.label,
    p_sort_order: input.sort_order ?? 0,
    p_active: input.active ?? true,
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function deleteFormFieldOption(id: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('delete_form_field_option', { p_id: id });
  if (error) throw new Error(formatSupabaseError(error));
}

export type DeliveryScheduleRoute = {
  id: string;
  postal_prefix: string;
  territory: string;
  schedule: string;
  remarks: string;
  active: boolean;
  sort_order: number;
};

export async function fetchDeliveryScheduleRoutes(activeOnly = false): Promise<DeliveryScheduleRoute[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('list_delivery_schedule_routes', {
    p_active_only: activeOnly,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as DeliveryScheduleRoute[];
}

export async function lookupDeliveryScheduleByPostal(
  postal: string,
): Promise<DeliveryScheduleRoute | null> {
  const trimmed = postal.trim();
  if (!trimmed) return null;
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('lookup_delivery_schedule_by_postal', {
    p_postal: trimmed,
  });
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return data as DeliveryScheduleRoute;
}

export async function upsertDeliveryScheduleRoute(input: {
  id?: string;
  postal_prefix: string;
  territory?: string;
  schedule?: string;
  remarks?: string;
  active?: boolean;
  sort_order?: number;
}): Promise<DeliveryScheduleRoute> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('upsert_delivery_schedule_route', {
    p_id: input.id ?? null,
    p_postal_prefix: input.postal_prefix,
    p_territory: input.territory ?? '',
    p_schedule: input.schedule ?? '',
    p_remarks: input.remarks ?? '',
    p_active: input.active ?? true,
    p_sort_order: input.sort_order ?? 0,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return data as DeliveryScheduleRoute;
}

export async function deleteDeliveryScheduleRoute(id: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.rpc('delete_delivery_schedule_route', { p_id: id });
  if (error) throw new Error(formatSupabaseError(error));
}

/** Infer AM/PM from schedule/remarks guideline text. */
export function sessionFromDeliverySchedule(schedule: string, remarks = ''): 'AM' | 'PM' {
  const blob = `${schedule} ${remarks}`.toUpperCase();
  if (/\bPM\b/.test(blob)) return 'PM';
  return 'AM';
}

export function postalPrefixFromCode(postal: string): string {
  const digits = postal.replace(/\D/g, '');
  if (digits.length < 2) return '';
  return digits.slice(0, 2);
}

export async function fetchApplicationFormSharePointSettings(): Promise<ApplicationFormSharePointSettings | null> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from('sharepoint_settings').select('*').eq('id', 1).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  return (data as ApplicationFormSharePointSettings | null) ?? null;
}

export async function saveApplicationFormSharePointSettings(input: {
  enabled: boolean;
  site_hostname: string;
  site_path: string;
  folder_path: string;
  workbook_name: string;
  sheet_name: string;
}): Promise<ApplicationFormSharePointSettings> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('save_sharepoint_settings', {
    p_enabled: input.enabled,
    p_site_hostname: input.site_hostname,
    p_site_path: input.site_path,
    p_folder_path: input.folder_path,
    p_workbook_name: input.workbook_name,
    p_sheet_name: input.sheet_name,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return data as ApplicationFormSharePointSettings;
}

export async function saveCffSharePointSettings(input: {
  enabled: boolean;
  site_hostname: string;
  site_path: string;
  folder_path: string;
}): Promise<ApplicationFormSharePointSettings> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('save_cff_sharepoint_settings', {
    p_enabled: input.enabled,
    p_site_hostname: input.site_hostname,
    p_site_path: input.site_path,
    p_folder_path: input.folder_path,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return data as ApplicationFormSharePointSettings;
}

export async function invokeApplicationFormSharePointSync(submissionId?: string): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(
      submissionId
        ? { action: 'sync_submission', submission_id: submissionId }
        : { action: 'sync_all' },
    ),
  });

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(String(body.error ?? `SharePoint sync failed (${res.status})`));
  }
  return body;
}

async function invokeApplicationFormSharePointAction(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(String(body.error ?? `SharePoint action failed (${res.status})`));
  }
  return body;
}

export async function testApplicationFormSharePointConnection(input: {
  target: 'account-creation' | 'cff';
  site_hostname: string;
  site_path: string;
  folder_path: string;
}): Promise<Record<string, unknown>> {
  return invokeApplicationFormSharePointAction({
    action: input.target === 'cff' ? 'test_cff' : 'test',
    target: input.target,
    site_hostname: input.site_hostname,
    site_path: input.site_path,
    folder_path: input.folder_path,
  });
}

function formatEdgeFunctionError(status: number, body: Record<string, unknown>): string {
  const fromBody =
    (typeof body.error === 'string' && body.error) ||
    (typeof body.message === 'string' && body.message) ||
    (typeof body.code === 'string' && body.code) ||
    '';
  if (status === 546) {
    return (
      fromBody ||
      'SharePoint sync ran out of Edge Function memory/CPU (HTTP 546). Redeploy the latest function and use the updated portal that fills Excel in the browser.'
    );
  }
  return fromBody || `SharePoint action failed (${status})`;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Keep blob URL briefly so slow browsers finish the download.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

async function downloadPdfBase64(b64: string, filename: string): Promise<void> {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  // Do not stamp a second logo — Graph PDF already has the template drawing;
  // overlay caused header overlap on converted PDFs.
  triggerBrowserDownload(new Blob([bytes], { type: 'application/pdf' }), filename);
}

export async function fetchCffMasterWorkbookBase64(
  applicationNo: string,
): Promise<ArrayBuffer | null> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!applicationNo.trim()) return null;

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({ action: 'get_cff_workbook', application_no: applicationNo }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    // First sync of the month: no workbook yet is fine.
    if (body.found === false || /not found|disabled/i.test(String(body.error ?? ''))) return null;
    throw new Error(formatEdgeFunctionError(res.status, body));
  }
  if (!body.found || typeof body.xlsx_base64 !== 'string' || !body.xlsx_base64) return null;
  const binary = atob(body.xlsx_base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export async function invokeCffSharePointSync(
  submissionId: string,
  sheetXlsxBase64: string,
  options: { skipPdf?: boolean; downloadPdf?: boolean } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!sheetXlsxBase64.trim()) throw new Error('Filled CFF Excel is required before SharePoint sync.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: 'sync_cff',
        submission_id: submissionId,
        sheet_xlsx_base64: sheetXlsxBase64,
        skip_pdf: options.skipPdf !== false,
        download_pdf: false,
      }),
    });
  } catch (err) {
    throw new Error(friendlyCffSyncError(err instanceof Error ? err.message : String(err)));
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyCffSyncError(formatEdgeFunctionError(res.status, body)));
  }
  return body;
}

/** Upload a fully built monthly CFF workbook (all tabs) to SharePoint.
 * Edge deletes the existing monthly file first, then uploads fresh.
 */
export async function uploadCffMonthlyWorkbook(
  yearMonth: string,
  xlsxBase64: string,
  options: { submissionId?: string } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!/^\d{4}-\d{2}$/.test(yearMonth.trim())) {
    throw new Error('year_month must be YYYY-MM');
  }
  if (!xlsxBase64.trim()) throw new Error('Monthly workbook is empty.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: 'upload_cff_month',
        year_month: yearMonth.trim(),
        xlsx_base64: xlsxBase64,
        submission_id: options.submissionId || undefined,
      }),
    });
  } catch (err) {
    throw new Error(friendlyCffSyncError(err instanceof Error ? err.message : String(err)));
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyCffSyncError(formatEdgeFunctionError(res.status, body)));
  }
  return body;
}

/**
 * Upload this CFF as a pristine single-sheet workbook (`CFF-….xlsx`).
 * Also deletes the legacy multi-tab monthly file that Excel Online corrupts.
 */
export async function rebuildCffMonthOnSharePoint(
  applicationNo: string,
  options: {
    submissionId: string;
    form?: import('../types/applicationForm').CustomerFeedbackFormData;
  },
): Promise<Record<string, unknown>> {
  const { buildCffSyncSheetBase64 } = await import('./customerFeedbackExcel');
  const {
    fetchCustomerFeedbackSubmission,
    normalizeCustomerFeedbackFormData,
  } = await import('./customerFeedbackSubmissions');

  let form = options.form;
  if (!form) {
    const row = await fetchCustomerFeedbackSubmission(options.submissionId);
    form = normalizeCustomerFeedbackFormData(row.form_data);
  }

  const sheetXlsxBase64 = await buildCffSyncSheetBase64(form, applicationNo);
  return invokeCffSharePointSync(options.submissionId, sheetXlsxBase64, { skipPdf: true });
}

export async function uploadCffPdfToSharePoint(
  submissionId: string,
  pdfBase64: string,
  options: { fileName?: string; asSignedCopy?: boolean } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!pdfBase64.trim()) throw new Error('Stamped PDF is required.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: 'upload_cff_pdf',
      submission_id: submissionId,
      pdf_base64: pdfBase64,
      pdf_file_name: options.fileName?.trim() || undefined,
      as_signed_copy: options.asSignedCopy === true,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyCffPdfError(formatEdgeFunctionError(res.status, body)));
  }
  return body;
}

export async function downloadCffPdfFromSharePoint(
  submissionId: string,
  sheetXlsxBase64: string,
  options: {
    stamp?: 'cancelled' | 'none';
    fileNameSuffix?: string;
    /** When false, PDF is still saved to SharePoint/Teams but not downloaded locally. */
    download?: boolean;
    /** Overlay circular red creator/submitter seal (e.g. near Submitted By). */
    creatorName?: string;
    /** Caption under the seal, e.g. invoice issuer. */
    creatorCaption?: string;
  } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!sheetXlsxBase64.trim()) throw new Error('Filled CFF Excel is required before PDF export.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: 'export_cff_pdf',
      submission_id: submissionId,
      sheet_xlsx_base64: sheetXlsxBase64,
      download_pdf: true,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyCffPdfError(formatEdgeFunctionError(res.status, body)));
  }
  let b64 = typeof body.pdf_base64 === 'string' ? body.pdf_base64 : '';
  if (!b64) throw new Error('PDF was not returned. Check SharePoint sync / Graph convert permissions.');

  const stamp = options.stamp ?? 'none';
  if (stamp === 'cancelled') {
    const { stampMemoPdfBase64 } = await import('./memoDocumentStamps');
    b64 = await stampMemoPdfBase64(b64, 'cancelled');
    // Graph uploaded an unstamped PDF — replace it on Teams with the stamped copy.
    try {
      await uploadCffPdfToSharePoint(submissionId, b64);
    } catch (uploadErr) {
      console.warn('Failed to upload stamped CANCELLED PDF to Teams:', uploadErr);
      throw new Error(
        uploadErr instanceof Error
          ? `PDF stamped locally, but Teams upload failed: ${uploadErr.message}`
          : 'PDF stamped locally, but Teams upload failed.',
      );
    }
  }

  const creatorName = options.creatorName?.trim();
  if (creatorName) {
    const { CFF_CREATOR_STAMP_SIZE_PT, stampCreatorNameOnPdfBase64 } = await import(
      './creatorNameStamp'
    );
    const caption = options.creatorCaption?.trim();
    b64 = await stampCreatorNameOnPdfBase64(b64, creatorName, {
      placement: 'cff-submitted-by',
      sizePt: CFF_CREATOR_STAMP_SIZE_PT,
      caption: caption || undefined,
    });
  }

  if (options.download !== false) {
    const baseName =
      (typeof body.application_no === 'string' && body.application_no) ||
      (typeof body.pdf_web_url === 'string'
        ? decodeURIComponent(body.pdf_web_url.split('/').pop() || '').replace(/\.pdf$/i, '')
        : '') ||
      'CFF';
    const suffix = options.fileNameSuffix?.trim() ? ` ${options.fileNameSuffix.trim()}` : '';
    const name = `${baseName.replace(/\.pdf$/i, '')}${suffix}`;
    await downloadPdfBase64(b64, `${name}.pdf`);
  }
  return body;
}

export async function syncMemoToSharePoint(
  submissionId: string,
  docxBase64: string,
  options: { skipPdf?: boolean } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!docxBase64.trim()) throw new Error('Filled memorandum Word file is required before SharePoint sync.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: 'sync_memo',
        submission_id: submissionId,
        docx_base64: docxBase64,
        skip_pdf: options.skipPdf !== false,
        download_pdf: false,
      }),
    });
  } catch (err) {
    throw new Error(friendlyMemoSyncError(err instanceof Error ? err.message : String(err)));
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyMemoSyncError(formatEdgeFunctionError(res.status, body)));
  }
  return body;
}

export async function downloadMemoPdfFromSharePoint(
  submissionId: string,
  docxBase64: string,
  options: {
    stamp?: 'posted' | 'cancelled' | 'none';
    fileNameSuffix?: string;
  } = {},
): Promise<Record<string, unknown>> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');
  if (!docxBase64.trim()) throw new Error('Filled memorandum Word file is required before PDF export.');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: 'export_memo_pdf',
      submission_id: submissionId,
      docx_base64: docxBase64,
      download_pdf: true,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(friendlyMemoPdfError(formatEdgeFunctionError(res.status, body)));
  }
  let b64 = typeof body.pdf_base64 === 'string' ? body.pdf_base64 : '';
  if (!b64) throw new Error('PDF was not returned. Check SharePoint sync / Graph convert permissions.');

  const stamp = options.stamp ?? 'none';
  if (stamp !== 'none') {
    const { stampMemoPdfBase64 } = await import('./memoDocumentStamps');
    b64 = await stampMemoPdfBase64(b64, stamp);
  }

  // Creator seal is embedded inline in the Word DOCX under Issued by (not a PDF overlay).

  const baseName =
    (typeof body.application_no === 'string' && body.application_no) ||
    (typeof body.docx_file_name === 'string' ? body.docx_file_name.replace(/\.docx$/i, '') : '') ||
    'MEMO';
  const suffix = options.fileNameSuffix?.trim() ? ` ${options.fileNameSuffix.trim()}` : '';
  const name = `${baseName}${suffix}`.replace(/\.pdf$/i, '');
  await downloadPdfBase64(b64, `${name}.pdf`);
  return body;
}

function friendlyMemoSyncError(raw: string): string {
  if (/Invalid action/i.test(raw)) {
    return (
      'Memo SharePoint sync is not available on the server yet. ' +
      'Deploy the updated application-form-sync edge function, then try again.'
    );
  }
  if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(raw)) {
    return (
      'Could not reach SharePoint sync (network/timeout). ' +
      'Wait a few seconds, then click Save again.'
    );
  }
  if (/423|locked|bypass-shared-lock|open in Excel|open in Teams|open in Word/i.test(raw)) {
    return (
      'SharePoint file is locked (open in Word or Teams). ' +
      'Close the memorandum file, then click Save again.'
    );
  }
  return raw;
}

function friendlyMemoPdfError(raw: string): string {
  if (/Invalid action/i.test(raw)) {
    return (
      'Memo PDF export is not available on the server yet. ' +
      'Deploy the updated application-form-sync edge function, then try Export PDF again.'
    );
  }
  if (/Timeout|timeout|TaskCanceled/i.test(raw)) {
    return (
      'Microsoft PDF service timed out. Wait a few seconds and click Export PDF again, ' +
      'or use Download DOCX and export PDF from Word/Teams.'
    );
  }
  if (/423|locked|open in Excel|open in Teams|open in Word/i.test(raw)) {
    return friendlyMemoSyncError(raw);
  }
  return raw;
}

function friendlyCffSyncError(raw: string): string {
  if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(raw)) {
    return (
      'Could not reach SharePoint sync (network/timeout). ' +
      'Close the monthly CFF Excel if it is open in Teams/Excel, wait a few seconds, then click Save changes again.'
    );
  }
  if (/423|locked|bypass-shared-lock|open in Excel|open in Teams/i.test(raw)) {
    return (
      'SharePoint workbook is locked (open in Excel or Teams). ' +
      'Close the monthly CFF Excel file, then click Save changes again.'
    );
  }
  return raw;
}

function friendlyCffPdfError(raw: string): string {
  if (/Timeout|timeout|TaskCanceled/i.test(raw)) {
    return (
      'Microsoft PDF service timed out. Wait a few seconds and click Export PDF again, ' +
      'or use Download Excel and export PDF from Excel/Teams.'
    );
  }
  if (/423|locked|open in Excel|open in Teams/i.test(raw)) {
    return friendlyCffSyncError(raw);
  }
  return raw;
}

export type ApplicationNotifyEvent = 'submission' | 'verified' | 'approved' | 'rejected';

export type ApplicationNotifyResult = {
  ok: boolean;
  sent?: { kind: string; to: string[] }[];
  skipped?: { kind: string; reason: string }[];
  /** Display names for success UI (emails still in `sent`). */
  recipientLabels?: { email: string; name: string }[];
  error?: string;
  reason?: string;
};

async function callApplicationFormNotify(body: Record<string, unknown>): Promise<ApplicationNotifyResult> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    return { ok: false, error: 'Supabase is not configured in this build (VITE_SUPABASE_URL / ANON_KEY).' };
  }

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-notify`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  const payload = (await res.json().catch(() => ({}))) as ApplicationNotifyResult & Record<string, unknown>;
  if (!res.ok) {
    return {
      ok: false,
      error: String(payload.error ?? `Notification request failed (HTTP ${res.status})`),
    };
  }

  return {
    ok: payload.ok !== false,
    sent: (payload.sent as ApplicationNotifyResult['sent']) ?? [],
    skipped: (payload.skipped as ApplicationNotifyResult['skipped']) ?? [],
    error: payload.error ? String(payload.error) : undefined,
    reason: payload.reason ? String(payload.reason) : undefined,
  };
}

export async function queueApplicationFormNotify(
  submissionId: string,
  event: ApplicationNotifyEvent,
): Promise<ApplicationNotifyResult> {
  try {
    return await callApplicationFormNotify({ submission_id: submissionId, event, action: 'notify' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Application form notification failed.';
    console.warn('Application form notification failed:', err);
    return { ok: false, error: message };
  }
}

export async function diagnoseApplicationFormNotify(): Promise<ApplicationNotifyResult & { config?: Record<string, unknown> }> {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !anonKey) {
      return { ok: false, error: 'Supabase is not configured in this build.' };
    }
    const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-notify`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ action: 'diagnose' }),
    });
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { ok: false, error: String(payload.error ?? `Diagnose failed (HTTP ${res.status})`) };
    }
    return { ok: true, config: (payload.config as Record<string, unknown>) ?? {} };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Diagnose failed.' };
  }
}

export async function sendApplicationFormTestEmail(
  toEmail?: string | null,
): Promise<ApplicationNotifyResult> {
  try {
    return await callApplicationFormNotify({
      action: 'test',
      to_email: String(toEmail ?? '').trim() || undefined,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Test email failed.' };
  }
}
